import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Grid,
  Paper,
  Alert,
  Popover,
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SellIcon from '@mui/icons-material/Sell';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import LabelIcon from '@mui/icons-material/Label';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LinkIcon from '@mui/icons-material/Link';
import SalesChannelForm from './SalesChannelForm';
import IntegrationStatus from './IntegrationStatus';
import IntegrationActions from './IntegrationActions';
import PlatformSelector from './PlatformSelector';
import ConflictManager from './ConflictManager';

const hasLabelRequired = (channel) => {
  return Array.isArray(channel.settings) &&
    channel.settings.some(s => s.key === 'labelRequired' && s.value === true);
};

const hasBarcodeRequired = (channel) => {
  return Array.isArray(channel.settings) &&
    channel.settings.some(s => s.key === 'barcodeRequired' && s.value === true);
};

const hasQuailHQEnabled = (channel) => {
  return Array.isArray(channel.settings) &&
    channel.settings.some(s => s.key === 'quailhqEnabled' && s.value === true);
};

const hasCommission = (channel) => {
  return Array.isArray(channel.settings) &&
    channel.settings.some(s => s.key === 'chargesCommission' && s.value === true);
};

const getCommissionRate = (channel) => {
  if (!Array.isArray(channel.settings)) return null;
  const entry = channel.settings.find(s => s.key === 'commissionPercentage');
  return entry != null ? Number(entry.value) : null;
};

const SalesChannelList = ({
  channels,
  isLoading = false,
  onEdit,
  onDelete,
  onAddChannel,
  channelFormData,
  onInputChange,
  onSubmit,
  isSubmitting,
  onCancelEdit,
  isEditing,
  // Integration props
  integrationStatuses = {},
  platforms = [],
  isConnecting = false,
  isDisconnecting = false,
  isSyncing = false,
  isImporting = false,
  isPulling = false,
  lastSyncResult = null,
  lastImportResult = null,
  lastPullResult = null,
  conflicts = [],
  isLoadingConflicts = false,
  isResolvingConflict = false,
  onFetchPlatforms,
  onConnect,
  onDisconnect,
  onReconnect,
  onSyncInventory,
  onImportSales,
  onPullInventory,
  onFetchConflicts,
  onResolveConflict,
  onResolveAllConflicts,
  onClearResults,
  quotaUsage,
  fetchQuotaUsage,
  quailhqStatuses = {},
}) => {
  const [deleteAnchorEl, setDeleteAnchorEl] = useState(null);
  const [deletingChannelId, setDeletingChannelId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [showPlatformSelector, setShowPlatformSelector] = useState(false);
  const [connectingChannelId, setConnectingChannelId] = useState(null);
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  const [showConflictManager, setShowConflictManager] = useState(false);
  const [conflictChannelId, setConflictChannelId] = useState(null);

  // Integration quota gating — limit of 0 or missing means feature not available
  const integrationQuota = quotaUsage?.connectedSalesChannels;
  const hasIntegrationFeature = integrationQuota != null && integrationQuota.limit > 0;
  const integrationQuotaReached = hasIntegrationFeature && integrationQuota.used >= integrationQuota.limit;

  const handleOpenAddForm = () => {
    onAddChannel();
    setShowFormDialog(true);
  };

  const handleOpenEditForm = (event, channel) => {
    onEdit(channel);
    setShowFormDialog(true);
  };

  const handleCloseForm = () => {
    setShowFormDialog(false);
    onCancelEdit();
  };

  const handleFormSubmit = async (e) => {
    await onSubmit(e);
    setShowFormDialog(false);
  };

  const handleDeleteClick = (event, channelId) => {
    setDeleteAnchorEl(event.currentTarget);
    setDeletingChannelId(channelId);
  };

  const handleDeleteConfirmClose = () => {
    setDeleteAnchorEl(null);
    setDeletingChannelId(null);
    setIsDeleting(false);
  };

  const handleDeleteConfirmed = async () => {
    if (deletingChannelId) {
      if (isDeleting) return;
      setIsDeleting(true);
      const deleted = await onDelete(deletingChannelId);
      if (deleted) {
        handleDeleteConfirmClose();
      } else {
        setIsDeleting(false);
      }
    }
  };

  const handleConnectClick = async (channelId) => {
    setConnectingChannelId(channelId);
    if (onFetchPlatforms) {
      await onFetchPlatforms();
    }
    setShowPlatformSelector(true);
  };

  const handlePlatformSelect = (platformId) => {
    if (connectingChannelId && onConnect) {
      onConnect(connectingChannelId, platformId);
    }
  };

  const handleClosePlatformSelector = () => {
    setShowPlatformSelector(false);
    setConnectingChannelId(null);
  };

  const handlePullInventory = async (channelId) => {
    const result = await onPullInventory(channelId);
    if (result && result.conflicts > 0) {
      setConflictChannelId(channelId);
      onFetchConflicts(channelId, 'pending');
      setShowConflictManager(true);
    }
  };

  const handleCloseConflicts = () => {
    setShowConflictManager(false);
    setConflictChannelId(null);
  };

  const conflictChannelName = conflictChannelId
    ? channels.find(ch => ch.id === conflictChannelId)?.name
    : '';

  // Determine sync/import result alert
  const resultErrors = lastSyncResult?.errors || lastImportResult?.errors || [];
  const resultAlert = lastSyncResult ? {
    severity: lastSyncResult.failed === 0 ? 'success' : (lastSyncResult.synced > 0 ? 'warning' : 'error'),
    message: `Synced ${lastSyncResult.synced} item${lastSyncResult.synced !== 1 ? 's' : ''}${lastSyncResult.failed > 0 ? `, ${lastSyncResult.failed} failed` : ''}`,
  } : lastImportResult ? {
    severity: lastImportResult.failed === 0 ? 'success' : (lastImportResult.imported > 0 ? 'warning' : 'error'),
    message: `Imported ${lastImportResult.imported} sale${lastImportResult.imported !== 1 ? 's' : ''}${lastImportResult.skipped > 0 ? `, ${lastImportResult.skipped} skipped` : ''}${lastImportResult.failed > 0 ? `, ${lastImportResult.failed} failed` : ''}`,
  } : null;

  return (
    <Box sx={{ py: 3 }}>
      {/* Sync/Import Result Alert */}
      {resultAlert && (
        <Alert
          severity={resultAlert.severity}
          onClose={onClearResults}
          sx={{ mb: 2 }}
          action={
            resultErrors.length > 0 ? (
              <Button color="inherit" size="small" onClick={() => setShowErrorDetails(true)} sx={{ textTransform: 'none' }}>
                View Details
              </Button>
            ) : undefined
          }
        >
          {resultAlert.message}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SellIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Sales Channels ({channels.length})
          </Typography>
        </Box>

        {/* Add Channel Button */}
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAddForm}
          sx={{
            textTransform: 'none',
            fontSize: '1rem',
          }}
        >
          Add Channel
        </Button>

        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, newView) => newView && setViewMode(newView)}
          size="small"
        >
          <ToggleButton value="grid" aria-label="grid view">
            <GridViewIcon />
          </ToggleButton>
          <ToggleButton value="list" aria-label="list view">
            <ViewListIcon />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Channels Grid or Empty State */}
      {isLoading && channels.length === 0 ? (
        viewMode === 'grid' ? (
          <Grid container spacing={2}>
            {Array.from({ length: 6 }).map((_, index) => (
              <Grid item xs={12} sm={6} md={4} key={`loading-channel-card-${index}`}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2 }}>
                  <Skeleton variant="text" width="60%" sx={{ mb: 2 }} />
                  <Skeleton variant="rectangular" height={80} />
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Default Location</TableCell>
                  <TableCell sx={{ fontWeight: 700, display: { xs: 'none', md: 'table-cell' } }}>Link</TableCell>
                  {hasIntegrationFeature && <TableCell sx={{ fontWeight: 700 }}>Integration</TableCell>}
                  <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.from({ length: 6 }).map((_, index) => (
                  <TableRow key={`loading-channel-row-${index}`}>
                    <TableCell><Skeleton variant="text" width="40%" /></TableCell>
                    <TableCell><Skeleton variant="text" width="40%" /></TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}><Skeleton variant="text" width="40%" /></TableCell>
                    {hasIntegrationFeature && <TableCell><Skeleton variant="text" width={100} /></TableCell>}
                    <TableCell align="center"><Skeleton variant="text" width={80} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      ) : channels.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            padding: 4,
            textAlign: 'center',
            backgroundColor: '#f5f5f5',
            borderRadius: 2,
            border: '1px dashed #ccc',
          }}
        >
          <SellIcon
            sx={{
              fontSize: 48,
              color: '#ccc',
              mb: 2,
              display: 'block',
            }}
          />
          <Alert severity="info">
            No sales channels found. Create your first channel above!
          </Alert>
        </Paper>
      ) : viewMode === 'grid' ? (
        <Grid container spacing={2}>
          {channels.map(channel => {
            const status = integrationStatuses[channel.id];
            const isConnected = status?.status === 'connected';
            const showConnectButton = hasIntegrationFeature && (!status || !status.status || status.status === 'disconnected');
            return (
              <Grid item xs={12} sm={6} md={4} key={channel.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: 4,
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                      {channel.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                      {hasLabelRequired(channel) && (
                        <Chip
                          label="Label"
                          size="small"
                          color="primary"
                          variant="outlined"
                          icon={<LabelIcon />}
                        />
                      )}
                      {hasBarcodeRequired(channel) && (
                        <Chip
                          label="Barcode"
                          size="small"
                          color="primary"
                          variant="outlined"
                          icon={<QrCode2Icon />}
                        />
                      )}
                      {hasQuailHQEnabled(channel) && (
                        <Chip
                          label="QuailHQ"
                          size="small"
                          color="primary"
                          variant="outlined"
                          icon={<Box component="img" src="/assets/quail-hq-logo.png" alt="QuailHQ" sx={{ height: 16, width: 'auto' }} />}
                        />
                      )}
                      {hasCommission(channel) && (
                        <Chip
                          label={`Commission ${getCommissionRate(channel) ?? ''}%`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                      {hasIntegrationFeature && <IntegrationStatus status={status} />}
                    </Box>
                  </CardContent>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                      {channel.defaultLocation || 'No default location set'}
                    </Typography>
                    {channel.channelLink && (
                      <Box sx={{ mt: 1 }}>
                        {channel.channelLink.startsWith('http://') || channel.channelLink.startsWith('https://') ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography
                              variant="body2"
                              component="a"
                              href={channel.channelLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                color: 'primary.main',
                                textDecoration: 'none',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: 200,
                                display: 'block',
                                '&:hover': { textDecoration: 'underline' },
                              }}
                            >
                              {channel.channelLink}
                            </Typography>
                            <OpenInNewIcon sx={{ fontSize: 14, color: 'primary.main', flexShrink: 0 }} />
                          </Box>
                        ) : (
                          <Typography variant="body2" noWrap>
                            {channel.channelLink}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </CardContent>
                  <CardActions sx={{ gap: 0.5, padding: 2, paddingTop: 0, flexWrap: 'wrap' }}>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={(e) => handleOpenEditForm(e, channel)} aria-label="Edit">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {showConnectButton && (
                      <Tooltip title={integrationQuotaReached ? `Connection limit reached (${integrationQuota.used}/${integrationQuota.limit})` : 'Connect to platform'}>
                        <span>
                          <IconButton size="small" color="primary" onClick={() => handleConnectClick(channel.id)} disabled={integrationQuotaReached} aria-label="Connect to platform">
                            <LinkIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {hasIntegrationFeature && (isConnected || status?.status === 'token_expired' || status?.status === 'error') && (
                      <IntegrationActions
                        status={status}
                        onDisconnect={async () => { const ok = await onDisconnect(channel.id); if (ok) fetchQuotaUsage?.(); return ok; }}
                        onReconnect={() => onReconnect(channel.id)}
                        onSyncInventory={(options) => onSyncInventory(channel.id, options)}
                        onImportSales={(dateRange) => onImportSales(channel.id, dateRange)}
                        onPullInventory={() => handlePullInventory(channel.id)}
                        isDisconnecting={isDisconnecting}
                        isSyncing={isSyncing}
                        isImporting={isImporting}
                        isPulling={isPulling}
                      />
                    )}
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={(e) => handleDeleteClick(e, channel.id)} aria-label="Delete">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Default Location</TableCell>
                <TableCell sx={{ fontWeight: 700, display: { xs: 'none', md: 'table-cell' } }}>Link</TableCell>
                {hasIntegrationFeature && <TableCell sx={{ fontWeight: 700 }}>Integration</TableCell>}
                <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {channels.map(channel => {
                const status = integrationStatuses[channel.id];
                const isConnected = status?.status === 'connected';
                const showConnectButton = hasIntegrationFeature && (!status || !status.status || status.status === 'disconnected');
                return (
                  <TableRow
                    key={channel.id}
                    sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {channel.name}
                        </Typography>
                        {hasLabelRequired(channel) && (
                          <Chip
                            label="Label"
                            size="small"
                            color="primary"
                            variant="outlined"
                            icon={<LabelIcon />}
                          />
                        )}
                        {hasBarcodeRequired(channel) && (
                          <Chip
                            label="Barcode"
                            size="small"
                            color="primary"
                            variant="outlined"
                            icon={<QrCode2Icon />}
                          />
                        )}
                        {hasQuailHQEnabled(channel) && (
                          <Chip
                            label="QuailHQ"
                            size="small"
                            color="primary"
                            variant="outlined"
                            icon={<Box component="img" src="/assets/quail-hq-logo.png" alt="QuailHQ" sx={{ height: 16, width: 'auto' }} />}
                          />
                        )}
                        {hasCommission(channel) && (
                          <Chip
                            label={`Commission ${getCommissionRate(channel) ?? ''}%`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="left">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {channel.defaultLocation || 'No default location set'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      {channel.channelLink && (channel.channelLink.startsWith('http://') || channel.channelLink.startsWith('https://')) ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography
                            variant="body2"
                            component="a"
                            href={channel.channelLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: 'primary.main',
                              textDecoration: 'none',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: 200,
                              display: 'block',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            {channel.channelLink}
                          </Typography>
                          <OpenInNewIcon sx={{ fontSize: 14, color: 'primary.main', flexShrink: 0 }} />
                        </Box>
                      ) : (
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {channel.channelLink || ''}
                        </Typography>
                      )}
                    </TableCell>
                    {hasIntegrationFeature && (
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IntegrationStatus status={status} />
                          {showConnectButton && (
                            <Tooltip title={integrationQuotaReached ? `Connection limit reached (${integrationQuota.used}/${integrationQuota.limit})` : 'Connect to platform'}>
                              <span>
                                <IconButton size="small" color="primary" onClick={() => handleConnectClick(channel.id)} disabled={integrationQuotaReached} aria-label="Connect to platform">
                                  <LinkIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    )}
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={(e) => handleOpenEditForm(e, channel)} aria-label="Edit">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {hasIntegrationFeature && (isConnected || status?.status === 'token_expired' || status?.status === 'error') && (
                          <IntegrationActions
                            status={status}
                            onDisconnect={async () => { const ok = await onDisconnect(channel.id); if (ok) fetchQuotaUsage?.(); return ok; }}
                            onReconnect={() => onReconnect(channel.id)}
                            onSyncInventory={(options) => onSyncInventory(channel.id, options)}
                            onImportSales={(dateRange) => onImportSales(channel.id, dateRange)}
                            onPullInventory={() => handlePullInventory(channel.id)}
                            isDisconnecting={isDisconnecting}
                            isSyncing={isSyncing}
                            isImporting={isImporting}
                            isPulling={isPulling}
                          />
                        )}
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={(e) => handleDeleteClick(e, channel.id)} aria-label="Delete">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Channel Form Dialog */}
      <Dialog
        open={showFormDialog}
        onClose={handleCloseForm}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <SalesChannelForm
            channelFormData={channelFormData}
            onInputChange={onInputChange}
            onSubmit={handleFormSubmit}
            isSubmitting={isSubmitting}
            onCancel={handleCloseForm}
            isEditing={isEditing}
          />
        </DialogContent>
      </Dialog>

      {/* Platform Selector Dialog */}
      <PlatformSelector
        open={showPlatformSelector}
        onClose={handleClosePlatformSelector}
        platforms={platforms}
        onSelect={handlePlatformSelect}
        isConnecting={isConnecting}
      />

      {/* Delete Confirmation Popover */}
      <Popover
        open={Boolean(deleteAnchorEl)}
        anchorEl={deleteAnchorEl}
        onClose={handleDeleteConfirmClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box sx={{ p: 2, maxWidth: 300 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Deleting a channel is irreversible. Are you sure?
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button onClick={handleDeleteConfirmClose} variant="outlined">
              Go Back
            </Button>
            <Button
              onClick={handleDeleteConfirmed}
              variant="contained"
              color="error"
              disabled={isDeleting}
            >
              {isDeleting ? <CircularProgress size={18} color="inherit" /> : 'Yes, Delete'}
            </Button>
          </Box>
        </Box>
      </Popover>

      {/* Error Details Dialog */}
      <Dialog
        open={showErrorDetails}
        onClose={() => setShowErrorDetails(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {lastSyncResult ? 'Sync Details' : 'Import Details'}
        </DialogTitle>
        <DialogContent>
          {resultErrors.length > 0 ? (
            <List dense>
              {resultErrors.map((err, index) => (
                <ListItem key={index}>
                  <ListItemText
                    primary={err.itemId || err.orderId || `Item ${index + 1}`}
                    secondary={err.error}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No errors to display.
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* Conflict Manager Dialog */}
      <ConflictManager
        open={showConflictManager}
        onClose={handleCloseConflicts}
        conflicts={conflicts}
        isLoading={isLoadingConflicts}
        isResolving={isResolvingConflict}
        onResolve={(conflictId, resolution) => onResolveConflict(conflictChannelId, conflictId, resolution)}
        onResolveAll={(resolution) => onResolveAllConflicts(conflictChannelId, resolution)}
        onFilterChange={(status) => onFetchConflicts(conflictChannelId, status)}
        channelName={conflictChannelName}
      />
    </Box>
  );
};

export default SalesChannelList;
